import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useLocation } from 'react-router';

import { useMutation } from '#src/hooks/graphql/useMutation.ts';

import type {
  StartFiatWalletIdentityVerificationMutation,
  StartFiatWalletIdentityVerificationMutationVariables,
} from './__generated__/useStartFiatWalletIdentityVerification.graphql.ts';
import {
  IFRAME_RETURN_URL,
  type JONUM_FLOW,
  buildReturnUrl,
} from './returnUrl.ts';

const START_FIAT_WALLET_IDENTITY_VERIFICATION_MUTATION = gql`
  mutation StartFiatWalletIdentityVerificationMutation(
    $input: startFiatWalletIdentityVerificationInput!
  ) {
    startFiatWalletIdentityVerification(input: $input) {
      identityVerification {
        id
        status
        hostedUrl
      }
      mangopayUser {
        id
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  StartFiatWalletIdentityVerificationMutation,
  StartFiatWalletIdentityVerificationMutationVariables
>;

type Options = { flow?: typeof JONUM_FLOW; framed?: boolean };

export const useStartFiatWalletIdentityVerification = ({
  flow,
  framed = false,
}: Options = {}) => {
  const { pathname, search } = useLocation();
  const [mutate, { loading }] = useMutation(
    START_FIAT_WALLET_IDENTITY_VERIFICATION_MUTATION,
    { showErrorsWithSnackNotification: true }
  );

  const startIdentityVerification = async () => {
    const returnUrl = framed
      ? IFRAME_RETURN_URL
      : buildReturnUrl({ pathname, search }, flow);
    const { data, success } = await mutate({
      variables: { input: { returnUrl } },
    });
    const hostedUrl =
      data?.startFiatWalletIdentityVerification?.identityVerification
        ?.hostedUrl;

    return success && hostedUrl ? hostedUrl : null;
  };

  return { startIdentityVerification, loading };
};
